import CaixaLocalRepository, { CAIXA_TIPO } from "@/modules/PdvDesktop/repositories/CaixaLocalRepository";
import ApiOfflineService from "@/modules/PdvDesktop/services/ApiOfflineService";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";
import PdvDesktopAuthService from "@/modules/PdvDesktop/services/PdvDesktopAuthService";
import TerminalConfigService from "@/modules/PdvDesktop/services/TerminalConfigService";
import { pagamentoMovimentaCaixa } from "@/modules/PdvDesktop/services/PagamentoCaixaClassifier";

function mapTipo(idTipo) {
  switch (Number(idTipo)) {
    case CAIXA_TIPO.ABERTURA:
      return "abertura";
    case CAIXA_TIPO.VENDA:
      return "venda";
    case CAIXA_TIPO.SANGRIA:
      return "sangria";
    case CAIXA_TIPO.SUPRIMENTO:
      return "suprimento";
    case CAIXA_TIPO.FECHAMENTO:
      return "fechamento";
    default:
      return "movimento";
  }
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * O backend persiste os campos do caixa em DATETIME, no fuso de São Paulo.
 * Enviar o ISO UTC diretamente faz o banco salvar, por exemplo, 16:48 como se
 * fosse horário local, embora a abertura tenha ocorrido às 13:48.
 */
export function formatarDataHoraApi(value) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value;

  return `${part("year")}-${part("month")}-${part("day")} ${part(
    "hour"
  )}:${part("minute")}:${part("second")}`;
}

function getDataMovimento(row) {
  return row.data_venda || row.data_fechamento || row.data_abertura || row.created_at || null;
}

function parseJson(value) {
  if (!value || typeof value !== "string") return null;

  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function getPagamentoMovimento(row) {
  const pagamentoJson = parseJson(row.pagamento);
  if (Array.isArray(pagamentoJson) || (pagamentoJson && typeof pagamentoJson === "object")) {
    return Array.isArray(pagamentoJson) ? pagamentoJson : [pagamentoJson];
  }

  const payloadJson = parseJson(row.payload_json);
  const forma = payloadJson?.forma_pagamento || null;
  if (forma && typeof forma === "object") {
    return [{
      ...forma,
      id: forma.id ?? forma.id_forma_pagamento ?? null,
      value: forma.value ?? forma.valor ?? toNumber(row.valor_movimento || row.valor_abertura),
      descricao: forma.descricao ?? row.pagamento ?? null,
    }];
  }

  if (Number(row.id_tipo) === CAIXA_TIPO.VENDA && row.pagamento) {
    return [{
      id: null,
      value: toNumber(row.valor_movimento || row.valor_abertura),
      descricao: row.pagamento,
    }];
  }

  return null;
}

function movimentoPertenceAoCaixa(row) {
  if (Number(row.id_tipo) !== CAIXA_TIPO.VENDA) return true;

  const pagamentos = getPagamentoMovimento(row) || [];
  return pagamentos.some((pagamento) => pagamentoMovimentaCaixa(pagamento));
}

function buildCaixaPayload(rows, terminalConfig, usuarioAtual) {
  const movimentosCaixa = rows.filter((row) => movimentoPertenceAoCaixa(row));
  const abertura = movimentosCaixa.find((row) => Number(row.id_tipo) === CAIXA_TIPO.ABERTURA) || movimentosCaixa[0] || null;
  const fechamento = movimentosCaixa.find((row) => Number(row.id_tipo) === CAIXA_TIPO.FECHAMENTO) || null;
  const uuidCaixa = abertura?.uuid_caixa || movimentosCaixa[0]?.uuid_caixa || null;
  const idUsuario = abertura?.id_usuario || movimentosCaixa.find((row) => row.id_usuario)?.id_usuario || usuarioAtual?.id_usuario || null;
  const nomeUsuario = abertura?.nome_usuario || movimentosCaixa.find((row) => row.nome_usuario)?.nome_usuario || usuarioAtual?.nome || null;

  const valorAbertura = movimentosCaixa
    .filter((row) => Number(row.id_tipo) === CAIXA_TIPO.ABERTURA)
    .reduce((sum, row) => sum + toNumber(row.valor_abertura || row.valor_movimento), 0);
  const valorFechamento = fechamento ? toNumber(fechamento.valor_movimento) : null;
  const valorSangrias = movimentosCaixa
    .filter((row) => Number(row.id_tipo) === CAIXA_TIPO.SANGRIA)
    .reduce((sum, row) => sum + toNumber(row.valor_movimento), 0);
  const valorSuprimentos = movimentosCaixa
    .filter((row) => Number(row.id_tipo) === CAIXA_TIPO.SUPRIMENTO)
    .reduce((sum, row) => sum + toNumber(row.valor_movimento), 0);
  const valorVendas = movimentosCaixa
    .filter((row) => Number(row.id_tipo) === CAIXA_TIPO.VENDA)
    .reduce((sum, row) => sum + toNumber(row.valor_movimento), 0);

  return {
    uuid_caixa: uuidCaixa,
    id_usuario: idUsuario,
    nome_usuario: nomeUsuario,
    status: fechamento ? "fechado" : "aberto",
    data_abertura: formatarDataHoraApi(abertura?.data_abertura),
    data_fechamento: formatarDataHoraApi(fechamento?.data_fechamento),
    valor_abertura: valorAbertura,
    valor_fechamento: valorFechamento,
    valor_sangrias: valorSangrias,
    valor_suprimentos: valorSuprimentos,
    valor_vendas: valorVendas,
    nome_terminal: abertura?.nome_terminal || terminalConfig.terminal_nome || null,
    movimentacoes: movimentosCaixa.map((row) => {
      const payload = parseJson(row.payload_json) || {};
      return {
        uuid: row.uuid,
        uuid_movimentacao: row.uuid,
        uuid_caixa: row.uuid_caixa || uuidCaixa,
        tipo: mapTipo(row.id_tipo),
        descricao: row.descricao || null,
        valor: toNumber(row.valor_movimento || row.valor_abertura),
        pagamento: getPagamentoMovimento(row),
        forma_pagamento: row.pagamento || null,
        id_usuario: row.id_usuario || idUsuario,
        nome_usuario: row.nome_usuario || nomeUsuario,
        device_id: row.device_id || terminalConfig.device_id || null,
        id_terminal_venda: row.id_terminal_venda || terminalConfig.id_terminal_venda || null,
        uuid_terminal: row.uuid_terminal || terminalConfig.uuid_terminal || null,
        id_empresa: row.id_empresa || terminalConfig.id_empresa || usuarioAtual?.id_empresa || null,
        id_nfe: row.id_nfe || payload.id_nfe || null,
        uuid_venda_terminal: payload.uuid_venda_terminal || null,
        tipo_operacao: payload.tipo || null,
        data: formatarDataHoraApi(getDataMovimento(row)),
        data_movimento: formatarDataHoraApi(getDataMovimento(row)),
        fechamento_as_cegas: payload.fechamento_as_cegas === true,
        fechamento_pagamentos: payload.fechamento_pagamentos || null,
        valor_esperado: payload.valor_esperado ?? null,
        diferenca: payload.diferenca ?? null,
      };
    }),
  };
}

function extractSyncError(error) {
  const data = error?.response?.data ?? error?.data ?? null;
  if (data) {
    if (typeof data === "string") return data;
    if (data.errors) {
      const details = JSON.stringify(data.errors);
      return data.message ? `${data.message}: ${details}` : details;
    }
    if (data.message) return data.message;
    if (data.msg) return data.msg;
    if (data.error) return data.error;
  }

  return error?.message || "Falha ao sincronizar caixas pendentes.";
}

async function markCaixaError(caixa, erro) {
  await CaixaLocalRepository.marcarErroSyncCaixa(caixa.uuid_caixa, erro);
  return {
    uuid_caixa: caixa.uuid_caixa,
    status_sync: "erro",
    erro,
  };
}

const CaixaSyncService = {
  async sincronizarCaixasPendentes() {
    await TerminalConfigService.garantirTerminalAutorizado({ forcar: true });

    const pendentesLocais = await CaixaLocalRepository.buscarPendentesSync();
    if (!Array.isArray(pendentesLocais) || pendentesLocais.length === 0) {
      return { status: true, caixas: [] };
    }

    const movimentosIgnorados = pendentesLocais.filter((row) => !movimentoPertenceAoCaixa(row));
    await Promise.all(
      movimentosIgnorados.map((row) => CaixaLocalRepository.marcarSincronizado(row.uuid, {}))
    );
    const pendentes = pendentesLocais.filter((row) => movimentoPertenceAoCaixa(row));
    if (pendentes.length === 0) {
      return { status: true, caixas: [] };
    }

    const movimentacoesPendentes = pendentes.map((row) => ({
      uuid: row.uuid,
      uuid_movimentacao: row.uuid,
      uuid_caixa: row.uuid_caixa,
      tipo: mapTipo(row.id_tipo),
      valor: toNumber(row.valor_movimento || row.valor_abertura),
      id_usuario: row.id_usuario ?? null,
      device_id: row.device_id ?? null,
      id_terminal_venda: row.id_terminal_venda ?? null,
      status_sync: row.status_sync,
      ultimo_erro_sync: row.ultimo_erro_sync ?? null,
      created_at: row.created_at ?? null,
      updated_at: row.updated_at ?? null,
    }));
    console.log("[PDV_CAIXA_MOVIMENTACOES_PENDENTES]", movimentacoesPendentes);

    const usuarioAtual = await PdvDesktopAuthService.getUsuarioAtual();
    if (!usuarioAtual || !usuarioAtual.id_usuario) {
      const erro = "Usuário do operador não encontrado. Faça login novamente.";
      await Promise.all(pendentes.map((movimento) => CaixaLocalRepository.marcarErroSync(movimento.uuid, erro)));
      throw new Error(erro);
    }

    const terminalConfig = await AppConfigRepository.getTerminalConfig();
    const uuidTerminal = terminalConfig?.uuid_terminal || null;
    const deviceId = terminalConfig?.device_id || null;
    const idTerminalVenda = terminalConfig?.id_terminal_venda || null;
    const idEmpresa = terminalConfig?.id_empresa || usuarioAtual?.id_empresa || null;
    const nomeTerminal = await AppConfigRepository.get("terminal_nome");

    if (!uuidTerminal) {
      const erro = "uuid_terminal não encontrado para sincronização do caixa.";
      await Promise.all(pendentes.map((movimento) => CaixaLocalRepository.marcarErroSync(movimento.uuid, erro)));
      throw new Error(erro);
    }
    if (!deviceId) {
      const erro = "device_id não encontrado para sincronização do caixa.";
      await Promise.all(pendentes.map((movimento) => CaixaLocalRepository.marcarErroSync(movimento.uuid, erro)));
      throw new Error(erro);
    }
    if (!idTerminalVenda) {
      const erro = "id_terminal_venda não encontrado para sincronização do caixa.";
      await Promise.all(pendentes.map((movimento) => CaixaLocalRepository.marcarErroSync(movimento.uuid, erro)));
      throw new Error(erro);
    }

    const uuidCaixas = [...new Set(pendentes.map((row) => row.uuid_caixa).filter(Boolean))];
    const caixasBrutas = await Promise.all(
      uuidCaixas.map(async (uuid_caixa) => {
        const rows = await CaixaLocalRepository.buscarMovimentosPorUuidCaixa(uuid_caixa);
        return buildCaixaPayload(rows, { ...terminalConfig, terminal_nome: nomeTerminal }, usuarioAtual);
      })
    );

    const caixas = [];
    const errosLocais = [];

    for (const caixa of caixasBrutas) {
      if (!caixa.uuid_caixa) {
        errosLocais.push(await markCaixaError(caixa, "uuid_caixa obrigatório para sincronização."));
        continue;
      }
      if (!caixa.id_usuario) {
        errosLocais.push(await markCaixaError(caixa, "Usuário do operador não encontrado. Faça login novamente."));
        continue;
      }
      if (!Array.isArray(caixa.movimentacoes) || caixa.movimentacoes.length === 0) {
        errosLocais.push(await markCaixaError(caixa, "Caixa sem movimentações para sincronizar."));
        continue;
      }
      caixas.push(caixa);
    }

    if (caixas.length === 0) {
      return { status: false, caixas: errosLocais };
    }

    const payload = {
      uuid_terminal: uuidTerminal,
      device_id: deviceId,
      id_terminal_venda: idTerminalVenda,
      id_empresa: idEmpresa,
      caixas,
    };

    console.log("[PDV_CAIXA_SYNC_PAYLOAD]", payload);

    let response;
    try {
      response = await ApiOfflineService.post("offline/caixa/sincronizar", payload);
    } catch (error) {
      const message = extractSyncError(error);
      await Promise.all(caixas.map((caixa) => CaixaLocalRepository.marcarErroSyncCaixa(caixa.uuid_caixa, message)));
      throw error;
    }

    const responseData = response?.data ?? response;
    console.log("[PDV_CAIXA_SYNC_RETORNO]", responseData);

    const caixasRetorno = Array.isArray(responseData?.caixas) ? responseData.caixas : [];
    const resultados = [...errosLocais];

    for (const caixa of caixas) {
      const caixaRetorno = caixasRetorno.find((item) => item?.uuid_caixa === caixa.uuid_caixa) || null;

      if (!caixaRetorno) {
        if (responseData?.status === true) {
          await CaixaLocalRepository.atualizarCaixaSync(caixa.uuid_caixa, {});
          for (const movimento of caixa.movimentacoes) {
            await CaixaLocalRepository.marcarSincronizado(movimento.uuid, {});
          }
          resultados.push({
            uuid_caixa: caixa.uuid_caixa,
            status_sync: "sincronizado",
            movimentacoes: caixa.movimentacoes.map((movimento) => ({
              uuid: movimento.uuid,
              status_sync: "sincronizado",
            })),
          });
        } else {
          const erro = "Resposta de sincronização sem detalhe para o caixa.";
          await CaixaLocalRepository.marcarErroSyncCaixa(caixa.uuid_caixa, erro);
          resultados.push({ uuid_caixa: caixa.uuid_caixa, status_sync: "erro", erro });
        }
        continue;
      }

      await CaixaLocalRepository.atualizarCaixaSync(caixa.uuid_caixa, caixaRetorno);

      const movimentosRetorno = Array.isArray(caixaRetorno.movimentacoes)
        ? caixaRetorno.movimentacoes
        : [];

      if (caixaRetorno.status_sync === "erro" || caixaRetorno.erro) {
        const erro = caixaRetorno.erro || "Erro ao sincronizar caixa.";
        await CaixaLocalRepository.marcarErroSyncCaixa(caixa.uuid_caixa, erro);
        resultados.push({
          uuid_caixa: caixa.uuid_caixa,
          status_sync: "erro",
          erro,
          movimentacoes: movimentosRetorno,
        });
        continue;
      }

      const movimentosResultado = [];
      for (const movimento of caixa.movimentacoes) {
        const movimentoRetorno = movimentosRetorno.find((item) => {
          return item?.uuid === movimento.uuid || item?.uuid_movimentacao === movimento.uuid;
        }) || null;

        if (!movimentoRetorno) {
          await CaixaLocalRepository.marcarSincronizado(movimento.uuid, {
            id_caixa_web: caixaRetorno.id_caixa_web,
          });
          movimentosResultado.push({
            uuid: movimento.uuid,
            status_sync: "sincronizado",
          });
          continue;
        }

        if (movimentoRetorno.status_sync === "erro" || movimentoRetorno.erro) {
          const erro = movimentoRetorno.erro || "Erro ao sincronizar movimentação de caixa.";
          await CaixaLocalRepository.marcarErroSync(movimento.uuid, erro);
          movimentosResultado.push({
            uuid: movimento.uuid,
            status_sync: "erro",
            erro,
          });
          continue;
        }

        await CaixaLocalRepository.marcarSincronizado(movimento.uuid, {
          ...movimentoRetorno,
          id_caixa_web: caixaRetorno.id_caixa_web,
        });
        movimentosResultado.push({
          uuid: movimento.uuid,
          id_movimentacao_web: movimentoRetorno.id_movimentacao_web,
          status_sync: "sincronizado",
        });
      }

      resultados.push({
        uuid_caixa: caixa.uuid_caixa,
        id_caixa_web: caixaRetorno.id_caixa_web,
        status_sync: "sincronizado",
        movimentacoes: movimentosResultado,
      });
    }

    return {
      status: resultados.every((item) => item.status_sync === "sincronizado"),
      caixas: resultados,
    };
  },
};

export default CaixaSyncService;
